/**
 * Milk Provider Transforms
 *
 * Converts API wire-format objects -> view-model types (Mother)
 * and form data -> API request bodies.
 */
import type { Mother, MotherRole } from '../types';
import type {
  ApiMilkProviderResponse,
  ApiMilkProviderDetail,
  PersonType,
  RegisterMilkProviderRequest,
  UpdateMilkProviderRequest,
  InlineExclusionRequest,
  ChildDetailRequest,
  ConsentDocumentItem,
} from '../types/milk-provider.types';
import type { DemographicsFormValues, ConsentFormValues } from '../types/registration';
import { formatDate } from '@tms-phoenix/ui-vue/utils/datetime.util';

// --- API -> View-model ------------------------------------------------------

/** Map API person_type to UI MotherRole. */
function personTypeToRole(personType: PersonType): MotherRole {
  switch (personType) {
    case 'MOTHER':
      return 'MOTHER';
    case 'DONOR':
      return 'DONOR';
    case 'BOTH':
    case 'MOTHER_AND_BOTH':
    case 'DONOR_AND_BOTH':
      return 'BOTH';
    default:
      return 'MOTHER';
  }
}

/** Convert an ApiMilkProviderResponse to the Mother view-model used by UI components. */
export function apiMilkProviderToMother(api: ApiMilkProviderResponse): Mother {
  const role = personTypeToRole(api.person_type);
  const isDonor = role === 'DONOR' || role === 'BOTH';

  return {
    id: api.person_id,
    name: `${api.first_name} ${api.last_name}`.trim(),
    dob: formatDateForDisplay(api.date_of_birth),
    medicalRecordNumber: api.mrn ?? '',
    role,
    isActive: api.is_active,
    isActiveDonor: api.is_active_donor ?? undefined,
    isActiveMother: api.is_active_mother ?? undefined,
    donorId: api.donor_code ?? undefined,
    donorStatus: isDonor ? (api.is_active ? 'active' : 'inactive') : undefined,
    screeningStatus: isDonor
      ? api.screening_status === 'APPROVED'
        ? 'approved'
        : api.screening_status === 'CLEARED'
          ? 'cleared'
          : api.screening_status === 'PENDING'
            ? 'pending'
            : api.screening_status === 'REJECTED'
              ? 'rejected'
              : api.screening_status === 'EXPIRED'
                ? 'expired'
                : api.screening_status === 'ON_HOLD'
                  ? 'on-hold'
                : api.screening_status === 'NOT_REQUIRED'
                  ? 'not-required'
                  : undefined
      : undefined,
    onboardingStatus: isDonor ? (api.is_active ? 'active-donor' : 'inactive') : undefined,
    email: api.email ?? undefined,
    phone: api.phone ?? undefined,
    milkStartDate: api.first_milk_date ?? undefined,
    donationConclusionDate: api.donation_conclusion_date ?? undefined,
    exclusionDates: api.exclusions
      ? Array.from(
          new Map(
            api.exclusions.map((e) => [
              `${e.exclusion_start_date}|${e.exclusion_end_date ?? ''}`,
              { start: e.exclusion_start_date, end: e.exclusion_end_date ?? '' },
            ]),
          ).values(),
        )
      : undefined,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
  };
}

// --- Form data -> API request body ------------------------------------------

/**
 * Convert an ApiMilkProviderDetail (GET /milkProviders/:person_id response)
 * to the Mother view-model. Extends the base transform with detail-only fields:
 * unit, bed, address, milkStartDate, and all exclusion dates.
 */
export function apiMilkProviderDetailToMother(p: ApiMilkProviderDetail): Mother {
  const base = apiMilkProviderToMother(p);
  // The API may return location as nested objects OR flat ID fields; handle both.
  const pAny = p as unknown as Record<string, unknown>;
  return {
    ...base,
    // Detail response ships a pre-formatted full name
    name: p.name ?? base.name,
    // Re-format dob consistently (base already does this, but detail may differ)
    dob: formatDateForDisplay(p.date_of_birth),
    // Location details only present in the detail response
    address: p.street_address ?? undefined,
    city: p.city ?? undefined,
    zipCode: p.zip_code ?? undefined,
    unit: p.unit?.unit_name ?? (typeof pAny.unit_id === 'string' ? pAny.unit_id : undefined),
    bed: p.bed?.bed_number ?? (typeof pAny.bed_id === 'string' ? pAny.bed_id : undefined),
    // Milk start date
    milkStartDate: p.first_milk_date ?? undefined,
    // Conclusion of donation date
    donationConclusionDate: p.donation_conclusion_date ?? undefined,
    // Exclusion dates from the exclusions array only (deduped; normalize to YYYY-MM-DD to collapse ISO variants)
    exclusionDates: Array.from(
      new Map(
        (p.exclusions ?? []).map((e) => [
          `${e.exclusion_start_date.split('T')[0]}|${(e.exclusion_end_date ?? '').split('T')[0]}`,
          { start: e.exclusion_start_date, end: e.exclusion_end_date ?? '' },
        ]),
      ).values(),
    ),
    // Rejection reason (only populated when screening_status is REJECTED)
    rejectionReason: p.rejection_reason ?? undefined,
  };
}

/** Map UI form roles array (checkbox values) to API PersonType. */
function rolesToPersonType(roles: string[]): PersonType {
  const isMother = roles.includes('mother');
  const isDonor = roles.includes('donor');
  if (isMother && isDonor) return 'BOTH';
  if (isDonor) return 'DONOR';
  return 'MOTHER';
}

/** Format an ISO / YYYY-MM-DD date string as DD/MM/YYYY for display. */
export function formatDateForDisplay(value: string | null | undefined): string {
  if (!value) return '';
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const result = formatDate(value, tz);
  return result === '\u2014' ? '' : result;
}

/**
 * Update-only variant of {@link toDateOnly}: distinguishes an explicitly
 * cleared field from one that was simply never touched.
 *
 * - Empty/blank input (user cleared the field)   -> null   (clear the column)
 * - Unparseable/impossible date (garbage input)   -> undefined (leave column untouched)
 * - Valid date                                    -> the YYYY-MM-DD string
 *
 * Using bare {@link toDateOnly} for PATCH payloads is unsafe: it returns
 * `undefined` for BOTH a cleared field and an invalid one, and the backend's
 * partial-update logic treats `undefined` as "don't touch this column" — so a
 * cleared date field would silently keep its previous value forever.
 */
function toDateOnlyOrNull(value: string | null | undefined): string | null | undefined {
  if (!value) return null;
  return toDateOnly(value);
}

/** Convert a YYYY-MM-DD or ISO string from a date input to YYYY-MM-DD.
 *  Performs round-trip validation so impossible dates like 2024-02-31 are
 *  rejected rather than silently normalised by the JS Date constructor.
 */
function toDateOnly(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  // Already YYYY-MM-DD — validate via round-trip
  const plain = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (plain) {
    const [, y, m, d] = plain;
    const date = new Date(`${y}-${m}-${d}T00:00:00Z`);
    if (
      isNaN(date.getTime()) ||
      date.getUTCFullYear() !== Number(y) ||
      date.getUTCMonth() + 1 !== Number(m) ||
      date.getUTCDate() !== Number(d)
    ) {
      return undefined; // impossible calendar date
    }
    return value;
  }
  // ISO datetime -> extract the YYYY-MM-DD prefix from the original string and
  // validate it against the parsed date.  Comparing `d`'s UTC components against
  // `d.toISOString()` would be tautological, so instead we compare the literal
  // date portion of the input to what `Date` actually produced.
  const datePrefix = /^(\d{4}-\d{2}-\d{2})/.exec(value);
  const d = new Date(value);
  if (isNaN(d.getTime())) return undefined;
  const iso = d.toISOString().slice(0, 10);
  // If the input contained an explicit date portion, it must match the
  // normalised output; a mismatch means JS silently corrected an impossible date.
  if (datePrefix && datePrefix[1] !== iso) {
    return undefined;
  }
  return iso;
}

/**
 * Build child_details from the children form array (Donor-only).
 * Only includes rows where DOB is present (mandatory for donors).
 * Status is also mandatory; defaults to 'UNKNOWN' as a safety net
 * — Zod validation should catch missing status before we reach here.
 *
 * Returns undefined when the role is not donor-only or no valid rows exist.
 */
function buildChildDetails(
  children: DemographicsFormValues['children'],
  isDonorOnly: boolean,
): ChildDetailRequest[] | undefined {
  if (!isDonorOnly) return undefined;
  const details = (children ?? [])
    .filter((c) => c.dob)
    .map((c) => {
      const validDob = toDateOnly(c.dob);
      if (!validDob) return null; // impossible / unparseable date — skip row
      const statusMap: Record<string, ChildDetailRequest['child_status']> = {
        fullterm: 'FULL_TERM',
        full_term: 'FULL_TERM',
        alive: 'FULL_TERM',
        premature: 'PREMATURE',
        deceased: 'DECEASED',
        dead: 'DECEASED',
        unknown: 'UNKNOWN',
      };
      const normalizedStatus = (c.status ?? '').toLowerCase().replace(/\s+/g, '_');
      return {
        child_dob: validDob,
        child_status: statusMap[normalizedStatus] ?? 'UNKNOWN',
      };
    })
    .filter((d): d is ChildDetailRequest => d !== null);
  return details.length > 0 ? details : undefined;
}

/**
 * Build a RegisterMilkProviderRequest from the multi-step form data.
 *
 * @param demographics  Step 1 form values
 * @param consent       Step 2 form values
 * @param staffId       The current user's ID (used as consent recorded_by)
 * @param hospitalId    Current hospital context
 */
export function buildRegisterRequest(
  demographics: DemographicsFormValues,
  consent: ConsentFormValues,
  staffId: string,
  hospitalId: string,
): RegisterMilkProviderRequest {
  const exclusions: InlineExclusionRequest[] = (demographics.exclusionDates ?? [])
    .filter((e) => e.start)
    .map((e) => ({
      exclusion_start_date: toDateOnly(e.start) ?? e.start,
      exclusion_end_date: toDateOnly(e.end) ?? null,
      recorded_by: staffId,
      exclusion_type: 'OTHER' as const,
    }));

  const personType = rolesToPersonType(demographics.role ?? []);
  const isDonorOnly = personType === 'DONOR';
  const hasDonorRole = personType !== 'MOTHER';
  const childDetails = buildChildDetails(demographics.children, isDonorOnly);

  return {
    hospital_id: hospitalId,
    person_type: personType,
    first_name: demographics.firstName,
    last_name: demographics.lastName,
    date_of_birth: toDateOnly(demographics.dateOfBirth),
    mrn: demographics.mrn?.trim() || undefined,
    phone: demographics.phone || undefined,
    email: demographics.email || undefined,
    street_address: demographics.streetAddress || undefined,
    city: demographics.city || undefined,
    zip_code: demographics.zipCode || undefined,
    facility_id: demographics.hospitalLocation || undefined,
    unit_id: demographics.unit || undefined,
    bed_id: demographics.bed || undefined,
    child_details: childDetails,
    first_milk_date: toDateOnly(demographics.milkStartDate),
    // CDD is donor-specific; omit entirely (no DB row exists yet) for mother-only registrations.
    donation_conclusion_date: hasDonorRole ? toDateOnly(demographics.donationConclusionDate) : undefined,
    emergency_contact_name: demographics.emergencyContact?.name || undefined,
    emergency_contact_phone: demographics.emergencyContact?.phone || undefined,
    emergency_contact_relationship: demographics.emergencyContact?.relationship || undefined,
    is_consent_given: consent.agreedToTerms ?? false,
    staff_id: staffId,
    exclusions: exclusions.length > 0 ? exclusions : undefined,
  };
}

/**
 * Build an UpdateMilkProviderRequest from changed demographics form data.
 *
 * @param demographics    Updated demographics form values
 * @param hospitalId      Current hospital context
 * @param skipPersonType  When true, omit person_type from the PATCH body.
 *                        Used when the role change is handled by a separate
 *                        endpoint (e.g. convertToDonor) so the PATCH does
 *                        not race or conflict with that conversion.
 */
export function buildUpdateRequest(
  demographics: DemographicsFormValues,
  hospitalId: string,
  skipPersonType = false,
  staffId: string,
  isConsentGiven?: boolean,
): UpdateMilkProviderRequest {
  const personType = rolesToPersonType(demographics.role ?? []);
  const isDonorOnly = personType === 'DONOR';
  const hasDonorRole = personType !== 'MOTHER';
  const childDetails = buildChildDetails(demographics.children, isDonorOnly);

  // Build exclusion entries from the date-range picker values.
  // Drop entries whose start date is invalid (toDateOnly returns undefined).
  const exclusions: InlineExclusionRequest[] = (demographics.exclusionDates ?? [])
    .filter((e) => e.start)
    .reduce<InlineExclusionRequest[]>((acc, e) => {
      const start = toDateOnly(e.start);
      if (!start) return acc;           // invalid / unparseable date → skip entry
      acc.push({
        exclusion_start_date: start,
        exclusion_end_date: toDateOnly(e.end) ?? null,
        recorded_by: staffId,
        exclusion_type: 'OTHER' as const,
      });
      return acc;
    }, []);

  return {
    hospital_id: hospitalId,
    ...(!skipPersonType ? { person_type: personType } : {}),
    first_name: demographics.firstName || undefined,
    last_name: demographics.lastName || undefined,
    // Cleared fields must be sent as `null` (explicit clear), not `undefined`
    // ("don't touch this column") — the backend PATCH logic treats them
    // differently. See toDateOnlyOrNull()/`|| null` below.
    date_of_birth: toDateOnlyOrNull(demographics.dateOfBirth),
    mrn: demographics.mrn?.trim() || undefined,
    phone: demographics.phone || null,
    email: demographics.email || undefined,
    street_address: demographics.streetAddress || null,
    city: demographics.city || null,
    zip_code: demographics.zipCode || null,
    facility_id: demographics.hospitalLocation || null,
    unit_id: demographics.unit || null,
    bed_id: demographics.bed || null,
    child_details: childDetails,
    first_milk_date: toDateOnlyOrNull(demographics.milkStartDate),
    // CDD is donor-specific; send explicit null (not undefined) so a record that just
    // lost its donor role has any pre-existing donation_conclusion_date cleared in the DB.
    donation_conclusion_date: hasDonorRole ? toDateOnlyOrNull(demographics.donationConclusionDate) : null,
    emergency_contact_name: demographics.emergencyContact?.name || null,
    emergency_contact_phone: demographics.emergencyContact?.phone || null,
    emergency_contact_relationship: demographics.emergencyContact?.relationship || null,
    exclusions: exclusions.length > 0 ? exclusions : undefined,
    ...(isConsentGiven !== undefined ? { is_consent_given: isConsentGiven, staff_id: staffId } : {}),
  };
}

// --- Consent helpers --------------------------------------------------------

/**
 * Find the active granted consent record from a list of consent documents.
 *
 * The array has no guaranteed ordering and may contain multiple records
 * (e.g. a withdrawn record alongside a later granted one, or records for
 * different purposes). This helper deterministically selects the
 * most-recently-granted record where `is_consent_given` is `true` and
 * `withdrawn_at` is absent.
 *
 * Returns `undefined` when no matching record exists.
 */
export function findActiveGrantedConsent(
  consentDocs: ConsentDocumentItem[] | undefined,
): ConsentDocumentItem | undefined {
  if (!consentDocs || consentDocs.length === 0) return undefined;

  return consentDocs
    .filter((d) => d.is_consent_given && !d.withdrawn_at)
    .sort((a, b) => new Date(b.granted_at).getTime() - new Date(a.granted_at).getTime())[0];
}
